import { Injectable, Signal, computed, inject } from '@angular/core';
import { SME_SESSION } from '../tokens/sme-session.token';
import { SmePermissionsApi } from '../tokens/sme-permissions.token';
import { SmePermission, SmeRoleScope, normaliseRole, normaliseRoles } from '../models/sme-permission.model';

/**
 * REST-only `SmePermissionsApi` (EM-307, re-sourced onto the server definition by EM-937 / F6).
 *
 * ## What changed and why
 *
 * This service used to carry three static `ReadonlySet`s (`ACCOUNTANT` ⊂ `CFO` ⊂ `OWNER`) plus a
 * hard-coded €25,000 ceiling — a hand-maintained mirror of the backend `RolePermissionMatrix`.
 * It had drifted: `CHANGE_PIN` was a CFO permission the portal did not know about, so a CFO was
 * silently greyed out of an action they were entitled to.
 *
 * FR-4.4 / FR-4.6 require ONE definition driving both gates. So the sets are gone: `permissions`
 * is now the server-computed **union** of the active rep's roles, delivered on the existing
 * `GET /me/session` (no new endpoint — Rule 17), and the ceiling is the server-provided
 * `paymentCeilingEur`. The role matrix is no longer represented in this file at all.
 *
 * ## What deliberately did NOT change
 *
 * The `SmePermissionsApi` surface — `roleScope`, `permissions`, `can(action, amountEur?)`,
 * `canPerform(...)`. `shared/directives/can-perform.directive.ts` (`*lendoxCanPerform`),
 * `shared/sme/role-chip/`, and every `perms.can(...)` call across payments, card, financing and
 * profile keep compiling and behaving identically — only the *source* of the data moved.
 *
 * The backend remains the SOLE decision-maker: this powers the role chip and convenience greying,
 * and a forced call still returns the RFC-7807 403 (Rule 09). Greying is never the only guard.
 */
@Injectable()
export class SmePermissionsService implements SmePermissionsApi {
  private readonly session = inject(SME_SESSION);

  /** The active rep's primary role scope, canonical uppercase. */
  readonly roleScope = computed<SmeRoleScope | null>(() => normaliseRole(this.session.roleScope()));

  /**
   * The active rep's FULL role set (FR-4.2 / D4) — canonical uppercase, normalised once here.
   * Falls back to the single `roleScope` for a session resolved before the multi-role fields
   * existed, so a grandfathered response still renders a chip rather than nothing (D8).
   */
  readonly roleScopes = computed<SmeRoleScope[]>(() => {
    const fromSession = normaliseRoles(this.session.session()?.roleScopes);
    if (fromSession.length > 0) {
      return fromSession;
    }
    const primary = this.roleScope();
    return primary ? [primary] : [];
  });

  /**
   * The EFFECTIVE permission union, computed server-side (FR-4.4).
   *
   * Empty until the session resolves — and empty **fails closed**: every action greys out rather
   * than defaulting permissively. That is the correct posture, and it matches the backend, which
   * denies an empty role set outright.
   */
  readonly permissions = computed<ReadonlySet<SmePermission>>(() => new Set(this.session.session()?.permissions ?? []));

  /**
   * The union's payment ceiling in euros; `null` = unbounded. Server-provided — the portal holds
   * no €25,000 literal. Under multi-role the server already applied the union-max rule, so a rep
   * holding {OWNER, CFO} correctly reports unbounded rather than the CFO's cap.
   */
  readonly paymentCeilingEur = computed<number | null>(() => this.session.session()?.paymentCeilingEur ?? null);

  canPerform(action: SmePermission, amountEur?: number): boolean {
    return this.evaluate(action, amountEur);
  }

  can(action: SmePermission, amountEur?: number): Signal<boolean> {
    return computed(() => this.evaluate(action, amountEur));
  }

  private evaluate(action: SmePermission, amountEur?: number): boolean {
    if (!this.permissions().has(action)) {
      return false;
    }
    // Ceiling mirror for convenience greying; the backend re-checks and is authoritative.
    if (action === 'INITIATE_PAYMENT' && amountEur != null) {
      const ceiling = this.paymentCeilingEur();
      if (ceiling != null && amountEur > ceiling) {
        return false;
      }
    }
    return true;
  }
}
